<template lang="pug">
    #mutipleFileThumbnail.v-container(
        class="pa-0"
        :class="{'dragging-container': isDraggingContainer}"
    )
        //- 隱藏的 file input，始終渲染供程式呼叫
        input(
            ref="newfilesInput"
            type="file"
            class="d-none"
            multiple
            :accept="accept"
            @change="uploadDocument($event, filesPreview.length, 'add')"
        )
        //- 縮圖卡片列表（有附件時顯示）
        div.attachment-preview-row(class="px-3 pt-3 mr-3" v-if="filesPreview.length > 0")
            v-tooltip(
                v-for="(file, k) in filesPreview"
                :key="k"
                bottom
                content-class="thumbnail-tooltip"
            )
                template(v-slot:activator="{ on, attrs }")
                    div.thumbnail-card(v-bind="attrs" v-on="on")
                        img.thumbnail-img(v-if="isImageType(file)" :src="file.previewUrl" :alt="file.previewName")
                        template(v-else)
                            div.filename-text {{file.previewName}}
                            div.file-badge {{getFileBadge(file)}}
                        button.remove-btn(
                            v-show="isVisable"
                            type="button"
                            :disabled="isUploading"
                            @click.stop="removeFile(file, k)"
                        )
                            v-icon(size='16' color="#444444") mdi-close
                span {{file.previewName}}
        //- 拖曳懸停提示文字（僅未達上限時顯示）
        div.drag-hint-overlay(v-if="isDraggingContainer && canAddMore")
            span {{$t('AGENT_FILE_ATTACHMENT.DRAG_HINT')}}
        //- 錯誤訊息 snackbar
        v-snackbar(
            v-model="errorAlertVisible"
            top
            color="error"
            timeout="3500"
        ) {{ errorMessage }}
        //- 橘色超限 Toast
        transition(name="exceed-toast-fade")
            div.exceed-toast(
                v-if="exceedToastVisible"
                :class="$vuetify.breakpoint.smAndDown ? 'exceed-toast--bottom' : 'exceed-toast--top'"
            )
                //- v-icon.mr-2(color="white" small) mdi-alert-circle-outline
                v-img.mr-2(src="@/assets/images/warning-icon.svg" max-width="18px"  height="18px")
                span {{$t('AGENT_FILE_ATTACHMENT.EXCEED_MAX_TOAST')}}
</template>

<script>
import i18n from '/common/plugins/vue-i18n.js';

export default {
    name: 'MultipleFileThumbnail',
    props: {
        accept: {
            type: String
        },
        uploadBtnText: {
            type: String,
            default: 'Upload'
        },
        progressBtnText: {
            type: String,
            default: 'Uploading...'
        },
        uploadedFiles: {
            type: Array,
            default: () => [],
            required: true
        },
        callback: {
            type: Function,
        },
        isUploading: {
            type: Boolean,
            default: false
        },
        schedule_access: {
            type: Boolean,
            default: false
        },
        isVisable: {
            type: Boolean,
            default: true
        },
        showFollowUpHint: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            uploading: false,
            isDeleting: false,
            isDraggingContainer: false,
            errorAlertVisible: false,
            exceedToastVisible: false,
            errorMessage: 'Something went wrong',
            settingSavedSuccess: false,
            filesPreview: [],
            files: [],
            Max_files: 3,
            Max_size_bytes: 20 * 1024 * 1024,
            file_type_text: this.accept.replaceAll(',', ''),
            lastPasteTimestamp: '',
            pasteSequence: 1,
        }
    },
    mounted() {
        if (this.uploadedFiles) {
            for (let i = 0; i < this.uploadedFiles.length; i++) {
                this.filesPreview.push({
                    previewType: this.uploadedFiles[i].file_type,
                    previewUrl: this.uploadedFiles[i].full_file_path,
                    previewName: this.uploadedFiles[i].name,
                    isDragging: false,
                    isLocal: false
                });
                this.files.push({
                    file_type: this.uploadedFiles[i].file_type,
                    full_file_path: this.uploadedFiles[i].full_file_path,
                    name: this.uploadedFiles[i].name
                });
            }
        }
        document.addEventListener('paste', this.handleGlobalPaste);
        document.addEventListener('dragover', this._docDragOver, true);
        document.addEventListener('drop', this._docDrop, true);
    },
    beforeDestroy() {
        document.removeEventListener('paste', this.handleGlobalPaste);
        document.removeEventListener('dragover', this._docDragOver, true);
        document.removeEventListener('drop', this._docDrop, true);
        clearTimeout(this._dragResetTimer);
        clearTimeout(this._exceedToastTimer);
    },
    methods: {
        // ─── Public methods ──────────────────────────────────────────────────────
        selectFiles() {
            if (this.isUploading) return;
            // if (!this.canAddMore) return;
            const fileInputRef = this.$refs.newfilesInput;
            if (fileInputRef) fileInputRef.click();
        },
        // 供父元件呼叫：將 File 物件加入附件清單並觸發上傳流程
        async addFileFromPaste(file) {
            if (this.filesPreview.length >= this.Max_files) {
                this.showExceedToast();
                return;
            }
            if (this.totalFilesSize + file.size > this.Max_size_bytes) {
                this.errorMessage = i18n.t('AGENT_FILE_ATTACHMENT.EXCEED_SIZE_TOAST');
                this.errorAlertVisible = true;
                return;
            }
            try {
                await this.previewFile(file, this.filesPreview.length, 'add');
                this.$emit('addAttachmentData', this.files);
            } catch (error) {
                console.error('[addFileFromPaste] error:', error);
            }
        },
        // 供父元件呼叫：清空所有附件（不觸發任何刪除 API）
        clearFiles() {
            this.filesPreview = [];
            this.files = [];
        },

        // ─── Toast ───────────────────────────────────────────────────────────────
        showExceedToast() {
            this.exceedToastVisible = true;
            clearTimeout(this._exceedToastTimer);
            this._exceedToastTimer = setTimeout(() => {
                this.exceedToastVisible = false;
            }, 3500);
        },

        // ─── Screenshot 自動命名 ──────────────────────────────────────────────────
        generateScreenshotName() {
            const now = new Date();
            const pad = n => String(n).padStart(2, '0');
            const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
            if (ts === this.lastPasteTimestamp) {
                this.pasteSequence += 1;
                return `screenshot_${ts}_${this.pasteSequence}.png`;
            }
            this.lastPasteTimestamp = ts;
            this.pasteSequence = 1;
            return `screenshot_${ts}.png`;
        },

        // ─── Document 層級拖曳偵測（用於 thread-input-box 範圍的視覺回饋）────────
        _docDragOver(event) {
            const path = event.composedPath ? event.composedPath() : [];
            const inCtx = path.some(el => el.classList && el.classList.contains('thread-input-box'));
            if (inCtx) {
                this.isDraggingContainer = true;
                clearTimeout(this._dragResetTimer);
                // dragover 停止觸發（離開區域）時自動重置
                this._dragResetTimer = setTimeout(() => {
                    this.isDraggingContainer = false;
                }, 200);
            }
        },
        _docDrop() {
            clearTimeout(this._dragResetTimer);
            this.isDraggingContainer = false;
        },

        // ─── 全域剪貼簿貼上（限 thread-input-box 範圍）───────────────────────────
        async handleGlobalPaste(event) {
            // 僅攔截來自 thread-input-box 內的貼上事件
            const path = event.composedPath ? event.composedPath() : [event.target];
            const inCtx = path.some(el => el.classList && el.classList.contains('thread-input-box'));
            if (!inCtx) return;

            const items = event.clipboardData && event.clipboardData.items
                ? Array.from(event.clipboardData.items)
                : [];
            const imageItem = items.find(item => item.type.startsWith('image/'));
            if (!imageItem) return; // 純文字：不攔截，讓文字正常貼入 textarea

            event.preventDefault(); // 有圖片：攔截，不貼文字

            const supportedMimes = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!supportedMimes.includes(imageItem.type)) {
                this.$emit('showErrorDialog', i18n.t('AGENT_FILE_ATTACHMENT.WRONGTYPEINFILE'));
                return;
            }

            const blob = imageItem.getAsFile();
            if (!blob) return;
            const filename = this.generateScreenshotName();
            const file = new File([blob], filename, { type: imageItem.type });
            await this.addFileFromPaste(file);
        },

        // ─── 內部檔案處理 ────────────────────────────────────────────────────────
        add(previewType, previewUrl, previewName, isDragging, isLocal = true) {
            this.filesPreview.push({ previewType, previewUrl, previewName, isDragging, isLocal });
        },
        update(previewType, previewUrl, previewName, isDragging, index, isLocal = true) {
            this.$set(this.filesPreview, index, { previewType, previewUrl, previewName, isDragging, isLocal });
        },
        async removeFile(file, index) {
            if (this.isUploading || this.isDeleting) return;
            if (file.isLocal !== false) {
                // 本地檔案（尚未上傳至伺服器）
                this.filesPreview.splice(index, 1);
                this.files.splice(index, 1);
                return;
            }
            // 已上傳至伺服器的檔案
            this.isDeleting = true;
            let data_file = this.files[index];
            this.$emit('editDeleteFile', data_file);
            this.isDeleting = false;
            this.filesPreview.splice(index, 1);
            this.files.splice(index, 1);
        },
        async uploadDocument(event, index, action) {
            if (this.isUploading) return;
            const files = event.target.files || [];
            const acceptList = this.accept ? this.accept.split(',').map(ext => ext.trim().toLowerCase()) : null;

            let fileQuota = this.files.length + files.length;
            if (fileQuota > this.Max_files) {
                this.showExceedToast();
                event.target.value = '';
                return;
            }
            const newFilesSize = Array.from(files).reduce((sum, f) => sum + f.size, 0);
            if (this.totalFilesSize + newFilesSize > this.Max_size_bytes) {
                this.errorMessage = i18n.t('AGENT_FILE_ATTACHMENT.EXCEED_SIZE_TOAST');
                this.errorAlertVisible = true;
                event.target.value = '';
                return;
            }
            try {
                const promises = [];
                for (let i = 0; i < files.length; i++) {
                    let fileName = files[i].name.toLowerCase();
                    let fileExt = '.' + fileName.split('.').pop();
                    try {
                        if (!acceptList || acceptList.includes(fileExt)) {
                            promises.push(this.previewFile(files[i], index + i, action));
                        } else {
                            this.$emit('showErrorDialog', i18n.t('AGENT_FILE_ATTACHMENT.WRONGTYPEINFILE'));
                        }
                    } catch (error) {
                        console.error('error:', error);
                    }
                }
                await Promise.all(promises);
                if (action !== 'reset') {
                    if (!this.schedule_access) {
                        this.$emit('saveFiles', this.files);
                    } else {
                        this.$emit('addAttachmentData', this.files);
                    }
                }
            } finally {
                event.target.value = '';
            }
        },
        previewFile(file, index, action) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const isImage = file.type.startsWith('image/');
                    const obj = {
                        previewType: file.type,
                        previewUrl: isImage ? reader.result : null,
                        previewName: file.name,
                        isDragging: false,
                        isLocal: true
                    };
                    if (action === 'reset') {
                        this.update(obj.previewType, obj.previewUrl, obj.previewName, obj.isDragging, index, true);
                        this.$set(this.files, index, file);
                    } else {
                        this.add(obj.previewType, obj.previewUrl, obj.previewName, obj.isDragging, true);
                        this.files.push(file);
                    }
                    resolve();
                };
                reader.onerror = (error) => {
                    console.error(`Error reading file ${file.name}: ${error}`);
                    reject(error);
                };
                reader.readAsDataURL(file);
            });
        },
        isImageType(file) {
            const type = (file.previewType || '').toLowerCase();
            if (type.startsWith('image/')) return true;
            const name = (file.previewName || '').toLowerCase();
            const ext = name.split('.').pop();
            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
        },
        getFileBadge(file) {
            const name = (file.previewName || '').toLowerCase();
            const ext = name.split('.').pop().toUpperCase();
            return ext || 'FILE';
        },
    },
    computed: {
        canAddMore() {
            return this.filesPreview.length < this.Max_files;
        },
        totalFilesSize() {
            return this.files.reduce((sum, f) => sum + (f.size || 0), 0);
        }
    },
    watch: {
        uploadedFiles: {
            handler(newVal) {
                this.filesPreview = [];
                let update_file = [];
                for (let i = 0; i < newVal.length; i++) {
                    this.filesPreview.push({
                        previewType: newVal[i].file_type,
                        previewUrl: newVal[i].full_file_path,
                        previewName: newVal[i].name,
                        isDragging: false,
                        isLocal: false
                    });
                    update_file.push({
                        file_type: newVal[i].file_type,
                        full_file_path: newVal[i].full_file_path,
                        name: newVal[i].name
                    });
                }
                this.files = update_file;
            },
            deep: true
        },
    }
}
</script>

<style scoped>
#mutipleFileThumbnail {
    position: relative;
}

#mutipleFileThumbnail.dragging-container {
    min-height: 60px;
    /* border: 2px solid #00B4C1; */
    border-radius: 8px;
    background-color: #f0fbfc;
    border: 1px solid #14C6DE;
}

.attachment-preview-row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 8px;
    height: 100px;
}

.thumbnail-card {
    position: relative;
    width: 33%;
    aspect-ratio: 1 / 1;
    border: 1px solid #CCCCCC;
    border-radius: 8px;
    overflow: hidden;
    background: #ffffff;
    flex-shrink: 0;
}

.file-badge {
    position: absolute;
    left: 7.5px;
    top: 58px;
    height: 18px;
    background: #F4F4F4;
    border: 1px solid #CCCCCC;
    border-radius: 4px;
    padding: 0 4px;
    font-size: 12px;
    color: #444444;
    font-family: 'Segoe UI', sans-serif;
    letter-spacing: 0.5px;
    line-height: 18px;
    white-space: nowrap;
}

@media (min-width: 768px) {
    .thumbnail-card {
        width: 100px;
        height: 100px;
    }
    .file-badge {
        top: 73px;
    }
}

.thumbnail-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.filename-text {
    position: absolute;
    left: 7.5px;
    top: 7.5px;
    width: 64px;
    font-size: 12px;
    color: #666666;
    font-family: 'Segoe UI', sans-serif;
    letter-spacing: 0.5px;
    line-height: 1.5;
    word-break: break-word;
    overflow: hidden;
    max-height: 54px;
}


.remove-btn {
    position: absolute;
    right: 4.5px;
    top: 5.5px;
    width: 20px;
    height: 20px;
    background: #E8E8E8;
    border-radius: 99px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: none;
    padding: 0;
    outline: none;
}

.remove-btn:hover {
    background: #D0D0D0;
}

.remove-btn:active {
    background: #BBBBBB;
}

.remove-btn:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.drag-hint-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #666666;
    pointer-events: none;
    background: rgba(255, 255, 255, 0.88);
    border-radius: 6px;
    z-index: 1;
}

/* 橘色超限 Toast */
.exceed-toast {
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    background-color: #FB8C00;
    color: #ffffff;
    padding: 14px 20px;
    border-radius: 4px;
    font-size: 14px;
    display: flex;
    align-items: center;
    white-space: nowrap;
    z-index: 9999;
    box-shadow: 0 3px 5px -1px rgba(0,0,0,.2), 0 6px 10px 0 rgba(0,0,0,.14), 0 1px 18px 0 rgba(0,0,0,.12);
    pointer-events: none;
}

.exceed-toast--top {
    top: 50px;
}

.exceed-toast--bottom {
    bottom: 200px;
}

.exceed-toast-fade-enter-active,
.exceed-toast-fade-leave-active {
    transition: opacity 0.2s ease;
}

.exceed-toast-fade-enter,
.exceed-toast-fade-leave-to {
    opacity: 0;
}
</style>

<style>
/* 縮圖卡片 tooltip — 非 scoped，因 Vuetify tooltip 渲染於 body */
.thumbnail-tooltip.v-tooltip__content {
    background-color: #ffffff !important;
    border: 1px solid #CCCCCC;
    color: #666666 !important;
    box-shadow: 0px var(--Spacingnone, 0px) var(--Spacing2, 8px) var(--Spacingnone, 0px) #99999980;
    opacity: 1 !important;
    font-size: 12px;
    line-height: 1.5;
    padding: 5px 8px;
    max-width: 240px;
    word-break: break-all;
}
</style>
